import { routing } from './routing'

export const CLICK_LOCALE_COOKIE = 'click-language'
export const MANUAL_LOCALE_COOKIE = 'mini-app-locale'

export type AppLocale = (typeof routing.locales)[number]

export function normalizeAppLocale(value?: string): AppLocale | null {
  if (!value) return null

  const normalized = value === 'kz' ? 'kk' : value.toLowerCase()
  return routing.locales.includes(normalized as AppLocale)
    ? (normalized as AppLocale)
    : null
}

type CookieReader = {
  get: (name: string) => { value: string } | undefined
}

type SessionLocaleOptions = {
  /** Fresh Click mini app open — stale manual locale must not win over click-language */
  ignoreManualLocale?: boolean
}

export function isFreshMiniAppEntry(
  searchParams: Pick<URLSearchParams, 'get'>
): boolean {
  return Boolean(
    searchParams.get('token') || searchParams.get('shipper_id')
  )
}

export function getTenantLocaleKey(shipperId?: string | null): string {
  return (shipperId || '').replace(/\./g, '_') || 'default'
}

export function getActiveShipperId(cookies: CookieReader): string {
  return (
    cookies.get('temp-shipper_id')?.value ||
    cookies.get('shipper_id')?.value ||
    ''
  )
}

export function getTenantPreferredLocale(
  cookies: CookieReader,
  shipperId?: string | null
): AppLocale | null {
  const resolvedShipperId = shipperId || getActiveShipperId(cookies)
  if (!resolvedShipperId) return null

  const tenantKey = getTenantLocaleKey(resolvedShipperId)
  return normalizeAppLocale(
    cookies.get(`default_locale_${tenantKey}`)?.value
  )
}

/**
 * Session/manual locale overrides Click locale.
 * Used by middleware and home-page redirect to stay in sync.
 */
export function getSessionPreferredLocale(
  cookies: CookieReader,
  options?: SessionLocaleOptions
): AppLocale | null {
  if (!options?.ignoreManualLocale) {
    const manualLocale = normalizeAppLocale(
      cookies.get(MANUAL_LOCALE_COOKIE)?.value
    )
    if (manualLocale) return manualLocale
  }

  return normalizeAppLocale(cookies.get(CLICK_LOCALE_COOKIE)?.value)
}

/**
 * Locale for URL routing inside a shipper mini-app.
 * Tenant default wins over Click host `click-language` so switching shippers
 * does not fight between middleware and the home page.
 */
export function resolveRoutingLocale(
  cookies: CookieReader,
  shipperId?: string | null,
  options?: SessionLocaleOptions
): AppLocale | null {
  const tenantLocale = getTenantPreferredLocale(cookies, shipperId)
  if (tenantLocale) return tenantLocale
  return getSessionPreferredLocale(cookies, options)
}
